import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { API, getContentSource } from "@/lib/constants";

const STEPS = [
  { id: "contact", label: "Contact" },
  { id: "business", label: "Business" },
  { id: "experience", label: "Experience" },
  { id: "project", label: "Project" },
];

const INDUSTRIES = [
  "Legal / Law Firm", "Healthcare / Medical", "Construction / Contractors",
  "Real Estate", "HVAC / Plumbing", "Fitness / Wellness", "Restaurants / Food",
  "Retail / E-commerce", "Technology / SaaS", "Financial Services",
  "Home Services", "Automotive", "Education", "Manufacturing", "Other"
];

const ServiceQuestionnaireModal = ({ isOpen, onClose, serviceName = "General" }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", website_url: "",
    business_type: "", industry: "", business_size: "",
    worked_with_agency: "", seo_understanding: "", business_scope: "",
    crm_tools: "", budget: "", timeline: "", additional_notes: ""
  });

  if (!isOpen) return null;

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const canAdvance = () => {
    if (step === 0) return form.first_name && form.last_name && form.email && form.phone;
    if (step === 1) return form.business_type && form.industry && form.business_size;
    if (step === 2) return form.worked_with_agency && form.seo_understanding && form.business_scope;
    if (step === 3) return form.budget && form.timeline;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/service-inquiry`, { ...form, service: serviceName });
      onClose();
      navigate("/thank-you/lead?from=service");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(0);
    setSubmitted(false);
    setForm({
      first_name: "", last_name: "", email: "", phone: "", website_url: "",
      business_type: "", industry: "", business_size: "",
      worked_with_agency: "", seo_understanding: "", business_scope: "",
      crm_tools: "", budget: "", timeline: "", additional_notes: ""
    });
    onClose();
  };

  const RadioOption = ({ field, value, label }) => (
    <button
      type="button"
      onClick={() => update(field, value)}
      className={`w-full text-left px-4 py-3 rounded-lg border transition-all text-sm ${
        form[field] === value
          ? "border-pink-500 bg-pink-500/10 text-white"
          : "border-white/10 bg-white/5 text-gray-400 hover:border-white/20 hover:bg-white/10"
      }`}
      data-testid={`radio-${field}-${value.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    >
      {label}
    </button>
  );

  if (submitted) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-lead-modal onClick={handleClose}>
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        <div className="relative bg-[#111] border border-white/10 rounded-2xl w-full max-w-lg p-8 text-center" onClick={e => e.stopPropagation()}>
          <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-pink-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">We've Got Your Info</h2>
          <p className="text-gray-400 mb-6">
            Thanks, {form.first_name}! We'll review your project details and reach out within 1 business day to discuss next steps.
          </p>
          <Button onClick={handleClose} className="bg-pink-500 hover:bg-pink-600 text-white px-8" data-testid="questionnaire-done-btn">
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-lead-modal onClick={handleClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        className="relative bg-[#111] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="web-design-questionnaire-modal"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#111] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Start Your Project</h2>
            <p className="text-gray-500 text-xs mt-0.5">Step {step + 1} of {STEPS.length} — {STEPS[step].label}</p>
          </div>
          <button onClick={handleClose} className="text-gray-500 hover:text-white p-1" data-testid="questionnaire-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        <div className="px-6 pt-4">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-pink-500" : "bg-white/10"}`} />
            ))}
          </div>
        </div>

        {/* Form Content */}
        <form
          id="service-questionnaire-form"
          name="service-questionnaire-form"
          className="px-6 py-5 space-y-4"
          onSubmit={(e) => { e.preventDefault(); if (step === STEPS.length - 1 && canAdvance() && !submitting) handleSubmit(); }}
        >
          {step === 0 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-400 text-xs font-medium mb-1.5 block">First Name *</label>
                  <Input name="first_name" value={form.first_name} onChange={e => update("first_name", e.target.value)} placeholder="John" className="bg-white/5 border-white/10 text-white" data-testid="input-first-name" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs font-medium mb-1.5 block">Last Name *</label>
                  <Input name="last_name" value={form.last_name} onChange={e => update("last_name", e.target.value)} placeholder="Smith" className="bg-white/5 border-white/10 text-white" data-testid="input-last-name" />
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1.5 block">Email *</label>
                <Input type="email" name="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="john@company.com" className="bg-white/5 border-white/10 text-white" data-testid="input-email" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1.5 block">Phone *</label>
                <Input type="tel" name="phone" value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="(214) 555-1234" className="bg-white/5 border-white/10 text-white" data-testid="input-phone" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1.5 block">Current Website URL</label>
                <Input name="website_url" value={form.website_url} onChange={e => update("website_url", e.target.value)} placeholder="https://yoursite.com (or leave blank)" className="bg-white/5 border-white/10 text-white" data-testid="input-website" />
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-2 block">What type of business do you have? *</label>
                <div className="grid grid-cols-2 gap-2">
                  {["B2B (Business to Business)", "B2C (Business to Consumer)", "E-commerce / Online Store", "Local Service Business", "SaaS / Tech Startup", "Non-Profit / Organization"].map(v => (
                    <RadioOption key={v} field="business_type" value={v} label={v} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-2 block">Industry *</label>
                <select
                  name="industry"
                  value={form.industry}
                  onChange={e => update("industry", e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white appearance-none cursor-pointer focus:border-pink-500 focus:outline-none"
                  data-testid="select-industry"
                >
                  <option value="" className="bg-[#111]">Select your industry</option>
                  {INDUSTRIES.map(i => <option key={i} value={i} className="bg-[#111]">{i}</option>)}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-2 block">Business Size *</label>
                <div className="grid grid-cols-2 gap-2">
                  {["Just Me (Solopreneur)", "2-10 Employees", "11-50 Employees", "51-200 Employees", "200+ Employees"].map(v => (
                    <RadioOption key={v} field="business_size" value={v} label={v} />
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-2 block">Have you worked with a marketing agency before? *</label>
                <div className="space-y-2">
                  {[
                    "Yes, and it went well",
                    "Yes, but it didn't work out",
                    "No, this would be our first time"
                  ].map(v => (
                    <RadioOption key={v} field="worked_with_agency" value={v} label={v} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-2 block">How familiar are you with SEO, AEO, and GEO? *</label>
                <div className="space-y-2">
                  {[
                    "Very familiar — we actively invest in it",
                    "Somewhat familiar — know the basics",
                    "Heard of SEO but not AEO/GEO",
                    "Not familiar at all"
                  ].map(v => (
                    <RadioOption key={v} field="seo_understanding" value={v} label={v} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-2 block">Local or national business? *</label>
                <div className="space-y-2">
                  {[
                    "Local (serve a specific city/region)",
                    "National (serve customers across the US)",
                    "Both local and national",
                    "International"
                  ].map(v => (
                    <RadioOption key={v} field="business_scope" value={v} label={v} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1.5 block">Do you use a CRM or marketing automation tools?</label>
                <Input name="crm_tools" value={form.crm_tools} onChange={e => update("crm_tools", e.target.value)} placeholder="e.g. HubSpot, Salesforce, Mailchimp, None" className="bg-white/5 border-white/10 text-white" data-testid="input-crm" />
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <input type="hidden" name="first_name" value={form.first_name} />
              <input type="hidden" name="last_name" value={form.last_name} />
              <input type="hidden" name="email" value={form.email} />
              <input type="hidden" name="phone" value={form.phone} />
              <input type="hidden" name="website_url" value={form.website_url} />
              <input type="hidden" name="business_type" value={form.business_type} />
              <input type="hidden" name="industry" value={form.industry} />
              <input type="hidden" name="business_size" value={form.business_size} />
              <input type="hidden" name="worked_with_agency" value={form.worked_with_agency} />
              <input type="hidden" name="seo_understanding" value={form.seo_understanding} />
              <input type="hidden" name="business_scope" value={form.business_scope} />
              <input type="hidden" name="crm_tools" value={form.crm_tools} />
              <input type="hidden" name="budget" value={form.budget} />
              <input type="hidden" name="timeline" value={form.timeline} />
              <input type="hidden" name="content_source" value={getContentSource()} />
              <div>
                <label className="text-gray-400 text-xs font-medium mb-2 block">What's your budget for this project? *</label>
                <div className="space-y-2">
                  {[
                    "Under $5,000",
                    "$5,000 - $10,000",
                    "$10,000 - $25,000",
                    "$25,000 - $50,000",
                    "$50,000+",
                    "Not sure yet"
                  ].map(v => (
                    <RadioOption key={v} field="budget" value={v} label={v} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-2 block">When do you need this completed? *</label>
                <div className="space-y-2">
                  {[
                    "ASAP (within 4 weeks)",
                    "1-2 months",
                    "2-3 months",
                    "3-6 months",
                    "No rush — planning ahead"
                  ].map(v => (
                    <RadioOption key={v} field="timeline" value={v} label={v} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1.5 block">Anything else we should know?</label>
                <textarea
                  name="additional_notes"
                  value={form.additional_notes}
                  onChange={e => update("additional_notes", e.target.value)}
                  placeholder="Tell us about your goals, must-have features, or any challenges..."
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:border-pink-500 focus:outline-none resize-none"
                  data-testid="input-notes"
                />
              </div>
            </>
          )}
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 bg-[#111] border-t border-white/10 px-6 py-4 flex items-center justify-between">
          {step > 0 ? (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)} className="text-gray-400 hover:text-white" data-testid="questionnaire-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          ) : <div />}

          {step < STEPS.length - 1 ? (
            <Button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!canAdvance()}
              className="bg-pink-500 hover:bg-pink-600 text-white disabled:opacity-40"
              data-testid="questionnaire-next-btn"
            >
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              type="submit"
              form="service-questionnaire-form"
              disabled={!canAdvance() || submitting}
              className="bg-pink-500 hover:bg-pink-600 text-white disabled:opacity-40"
              data-testid="questionnaire-submit-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Submit Project Details"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceQuestionnaireModal;
